// components/actions/ActionContext.tsx
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  ActionContext,
  type Action,
  type ActionStep,
  type ActionResultLink,
  type ActionContextType,
} from '@ds/actions/useActions';

export type { Action, ActionStep, ActionResultLink } from '@ds/actions/useActions';


export const ActionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [actions, setActions] = useState<Action[]>([]);

  const registerAction = useCallback((
    type: string,
    label: string,
    stepLabels?: string[],
    metadata?: Record<string, unknown>,
    description?: string,
    resultLink?: ActionResultLink
  ): string => {
    const id = `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const steps: ActionStep[] | undefined = stepLabels?.map((stepLabel, index) => ({
      id: `${id}-step-${index}`,
      label: stepLabel,
      status: 'pending' as const,
    }));

    const action: Action = {
      id,
      type,
      label,
      description,
      status: 'pending',
      createdAt: new Date(),
      steps,
      metadata,
      resultLink,
    };

    setActions(prev => [...prev, action]);
    return id;
  }, []);

  const updateActionStep = useCallback((
    actionId: string,
    stepId: string,
    status: ActionStep['status'],
    message?: string,
    progress?: number
  ) => {
    setActions(prev =>
      prev.map(action =>
        action.id === actionId && action.steps
          ? {
              ...action,
              steps: action.steps.map(step =>
                step.id === stepId
                  ? { ...step, status, message, progress }
                  : step
              ),
            }
          : action
      )
    );
  }, []);

  const completeAction = useCallback((actionId: string, result?: unknown, resultLink?: ActionResultLink) => {
    setActions(prev =>
      prev.map(action =>
        action.id === actionId
          ? {
              ...action,
              status: 'completed' as const,
              completedAt: new Date(),
              result,
              resultLink,
            }
          : action
      )
    );
  }, []);

  const failAction = useCallback((actionId: string, error: string, resultLink?: ActionResultLink) => {
    setActions(prev =>
      prev.map(action =>
        action.id === actionId
          ? {
              ...action,
              status: 'error' as const,
              error,
              completedAt: new Date(),
              ...(resultLink ? { resultLink } : {}),
            }
          : action
      )
    );
  }, []);

  const removeAction = useCallback((id: string) => {
    setActions(prev => prev.filter(a => a.id !== id));
  }, []);

  const clearCompleted = useCallback(() => {
    setActions(prev => prev.filter(a => a.status === 'pending' || a.status === 'error'));
  }, []);

  const getAction = useCallback((id: string) => {
    return actions.find(a => a.id === id);
  }, [actions]);

  const isActionInProgress = useCallback((
    type: string,
    metadata?: Record<string, unknown>
  ): boolean => {
    return actions.some(action => {
      if (action.status !== 'pending') return false;
      if (action.type !== type) return false;
      
      // If metadata is provided, check if it matches
      if (metadata) {
        return Object.entries(metadata).every(
          ([key, value]) => action.metadata?.[key] === value
        );
      }
      
      return true;
    });
  }, [actions]);

  // Auto-remove only COMPLETED actions after 30 seconds (keep errors).
  // Returning the same array reference when nothing is stale preserves
  // context identity and avoids re-rendering every useActions() consumer
  // every 5 seconds (the invoice peek was refetching on each tick).
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setActions(prev => {
        const next = prev.filter(action => {
          if (action.status === 'pending') return true;
          if (action.status === 'error') return true;
          if (!action.completedAt) return true;
          return now - action.completedAt.getTime() < 30000;
        });
        return next.length === prev.length ? prev : next;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const value = useMemo<ActionContextType>(
    () => ({
      actions,
      registerAction,
      updateActionStep,
      completeAction,
      failAction,
      removeAction,
      clearCompleted,
      getAction,
      isActionInProgress,
    }),
    [
      actions,
      registerAction,
      updateActionStep,
      completeAction,
      failAction,
      removeAction,
      clearCompleted,
      getAction,
      isActionInProgress,
    ],
  );

  return <ActionContext.Provider value={value}>{children}</ActionContext.Provider>;
};
